import axios from 'axios';

import config from '../../config/config';
import getFirstSubdomain from '../../shared-functions/DomainsplitFunction';

export interface BrandingData {
  shortName: string;
  clientName: string;
  logo: string;
  favicon: string;
  theme: any;
  footer: any;
  qrCode: string;
  webSocketUrl: string;
  currencyCode?: string;
}

const STORAGE_KEY = 'app_branding_config';
const STORAGE_DOMAIN_KEY = 'app_branding_domain';
const STORAGE_TIME_KEY = 'app_branding_time';
const STORAGE_VERSION_KEY = 'app_branding_version';
// Bump this whenever BrandingData gains fields that older cached payloads won't have
// (e.g. currencyCode/currencyIcon), so stale caches are treated as invalid and refetched.
const CACHE_SCHEMA_VERSION = '2';
/**
 * Service to handle dynamic branding operations
 */
const brandingService = {
  /**
   * Fetches branding configuration for the current domain.
   * @param {string} [domain] - Optional domain override.
   * @returns {Promise<BrandingData | null>} The fetched branding data.
   */
  fetchBranding: async (domain?: string): Promise<BrandingData | null> => {
    const targetDomain = domain || getFirstSubdomain();

    if (!targetDomain) {
      return null;
    }

    // Check if we already have valid branding in localStorage for this domain
    const cachedData = brandingService.getCachedBranding();
    if (cachedData) {
      return cachedData;
    }

    try {
      const response = await axios.get(
        `${config.commonApiUrl}api/saas-client-config/v1/client-config`,
        {
          headers: {
            Authorization: 'internal',
            domain: targetDomain,
          },
        },
      );

      if (response.data) {
        // Store branding data + domain key together so we can validate the cache later
        localStorage.setItem(STORAGE_KEY, JSON.stringify(response.data));
        localStorage.setItem(STORAGE_DOMAIN_KEY, targetDomain);
        localStorage.setItem(STORAGE_TIME_KEY, Date.now().toString()); // ✅ add this
        localStorage.setItem(STORAGE_VERSION_KEY, CACHE_SCHEMA_VERSION);
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Failed to fetch branding:', error);
      // On API failure, return cache only if it belongs to the same domain
      return brandingService.getCachedBranding();
    }
  },

  /**
   * Gets branding from localStorage cache, but ONLY if it belongs to the current subdomain.
   * Returns null if the cached domain doesn't match (prevents stale cross-domain data).
   * @returns {BrandingData | null} The cached branding data.
   */
  getCachedBranding: (): BrandingData | null => {
    const currentDomain = getFirstSubdomain();
    const cachedDomain = localStorage.getItem(STORAGE_DOMAIN_KEY);
    const cachedTime = localStorage.getItem(STORAGE_TIME_KEY);
    const cachedVersion = localStorage.getItem(STORAGE_VERSION_KEY);

    // If the cached domain doesn't match the current domain, discard the cache
    if (cachedDomain !== currentDomain) {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(STORAGE_DOMAIN_KEY);
      return null;
    }

    // Cache was written by an older schema (e.g. missing currencyCode/currencyIcon) — refetch
    if (cachedVersion !== CACHE_SCHEMA_VERSION) {
      brandingService.clearCache();
      return null;
    }

    // ❌ Time expired (1 day = 24 * 60 * 60 * 1000 ms)
    const ONE_DAY = 24 * 60 * 60 * 1000;

    if (cachedTime) {
      const isExpired = Date.now() - Number(cachedTime) > ONE_DAY;

      if (isExpired) {
        brandingService.clearCache();
        return null;
      }
    }
    const cached = localStorage.getItem(STORAGE_KEY);
    if (cached) {
      try {
        return JSON.parse(cached);
      } catch (error_) {
        return null;
      }
    }
    return null;
  },

  /**
   * Resets the favicon back to the default app icon.
   */
  resetFavicon: () => {
    const existingIcons = document.querySelectorAll("link[rel*='icon']");
    existingIcons.forEach((icon) => icon.parentNode?.removeChild(icon));

    const defaultLink = document.createElement('link');
    defaultLink.rel = 'icon';
    // defaultLink.type = 'image/png';
    defaultLink.href = '/favicon_energic.ico';
    document.head.appendChild(defaultLink);
  },

  /**
   * Clears branding cache and resets the favicon to default.
   */
  clearCache: () => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_DOMAIN_KEY);
    localStorage.removeItem(STORAGE_TIME_KEY);
    localStorage.removeItem(STORAGE_VERSION_KEY);
    brandingService.resetFavicon();
  },
};

export default brandingService;
